import type { InviteUserResponse, User, UserStatus } from '@stayafrica/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  ChipFilter,
  Container,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  KpiCard,
  NoPermissionState,
  PageHeader,
  RowActions,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  UserCell,
} from '@stayafrica/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  Check,
  KeyRound,
  MoreHorizontal,
  Pencil,
  Search,
  ShieldCheck,
  ShieldOff,
  UserPlus,
  Users as UsersIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useCan, useSession } from '@/features/auth/session';
import { rolesQueryOptions } from '@/features/roles/queries';
import { DeliveryLinkDialog } from '@/features/users/DeliveryLinkDialog';
import { UserFormDrawer } from '@/features/users/UserFormDrawer';
import { usersQueryOptions, type UsersFilters } from '@/features/users/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatRelative } from '@/lib/format';

export const Route = createFileRoute('/_app/admin/users/')({ component: UsersPage });

const STATUSES: UserStatus[] = ['ACTIVE', 'INVITED', 'DISABLED'];

function UsersPage() {
  const { t, i18n } = useTranslation(['users', 'common', 'errors', 'roles']);
  const session = useSession();
  const canRead = useCan('user.read');
  const canCreate = useCan('user.create');

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<UserStatus | undefined>(undefined);
  const [drawerUser, setDrawerUser] = useState<User | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Invitation créée : lien d'activation remis à l'écran (D-055).
  const [invited, setInvited] = useState<InviteUserResponse | null>(null);

  // Recherche : 200 ms de debounce, minimum 2 caractères (UX_ARCHITECTURE §11).
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.length >= 2 ? search : ''), 200);
    return () => window.clearTimeout(timer);
  }, [search]);

  const filters: UsersFilters = {
    ...(debounced ? { q: debounced } : {}),
    ...(status ? { status } : {}),
  };

  const query = useInfiniteQuery({ ...usersQueryOptions(filters), enabled: canRead });
  const rolesQuery = useQuery({ ...rolesQueryOptions(), enabled: canCreate });

  const users = query.data?.pages.flatMap((page) => page.data) ?? [];
  const roles = rolesQuery.data ?? [];

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const activeCount = users.filter((user) => user.status === 'ACTIVE').length;
  const invitedCount = users.filter((user) => user.status === 'INVITED').length;
  const disabledCount = users.filter((user) => user.status === 'DISABLED').length;
  const mfaCount = users.filter((user) => user.mfaEnabled).length;
  const customRoles = roles.filter((role) => !role.isSystem).length;

  const openInvite = () => {
    setDrawerUser(undefined);
    setDrawerOpen(true);
  };

  return (
    <Container>
      <PageHeader
        title={t('users:title')}
        subtitle={t('users:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('users:title') },
        ]}
        actions={
          <Can permission="user.create">
            <Button variant="primary" onClick={openInvite}>
              <UserPlus className="ico" aria-hidden="true" />
              {t('users:invite')}
            </Button>
          </Can>
        }
      />

      <div className="stack">
        <div className="grid grid-4">
          <KpiCard
            tone="violet"
            icon={<UsersIcon className="ico ico-16" aria-hidden="true" />}
            label={t('users:kpi.active')}
            value={activeCount}
            loading={query.isPending}
            footer={t('users:kpi.activeFoot', { count: invitedCount })}
          />
          <KpiCard
            tone="green"
            icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
            label={t('users:kpi.mfa')}
            value={mfaCount}
            unit={` / ${users.length}`}
            loading={query.isPending}
            footer={t('users:kpi.mfaFoot', { count: Math.max(users.length - mfaCount, 0) })}
          />
          <KpiCard
            tone="blue"
            icon={<KeyRound className="ico ico-16" aria-hidden="true" />}
            label={t('users:kpi.roles')}
            value={roles.length}
            loading={rolesQuery.isPending && canCreate}
            footer={t('users:kpi.rolesFoot', { count: customRoles })}
          />
          <KpiCard
            tone="amber"
            icon={<AlertTriangle className="ico ico-16" aria-hidden="true" />}
            label={t('users:kpi.disabled')}
            value={disabledCount}
            loading={query.isPending}
            footer={t('users:kpi.disabledFoot')}
          />
        </div>

        <Card>
          <CardHeader
            title={t('users:title')}
            icon={<UsersIcon className="ico ico-16" aria-hidden="true" />}
          />

          <Toolbar>
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('users:searchPlaceholder')}
              aria-label={t('users:searchLabel')}
              icon={<Search className="ico" aria-hidden="true" />}
              style={{ width: 240, height: 32, fontSize: 'var(--fs-13)' }}
            />
            <ChipFilter active={status === undefined} onClick={() => setStatus(undefined)}>
              {t('users:filters.all')}
            </ChipFilter>
            {STATUSES.map((value) => (
              <ChipFilter
                key={value}
                active={status === value}
                onClick={() => setStatus(status === value ? undefined : value)}
              >
                {t(`users:status.${value}`)}
              </ChipFilter>
            ))}
          </Toolbar>

          {query.isPending ? (
            <TableSkeleton rows={5} columns={6} label={t('common:states.loading')} />
          ) : query.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : users.length === 0 ? (
            <EmptyState
              icon={<UsersIcon className="ico ico-24" aria-hidden="true" />}
              title={debounced || status ? t('users:emptySearchTitle') : t('users:emptyTitle')}
              description={debounced || status ? t('users:emptySearchBody') : t('users:emptyBody')}
              action={
                debounced || status ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSearch('');
                      setDebounced('');
                      setStatus(undefined);
                    }}
                  >
                    {t('common:actions.clearFilters')}
                  </Button>
                ) : (
                  <Can permission="user.create">
                    <Button variant="primary" onClick={openInvite}>
                      <UserPlus className="ico" aria-hidden="true" />
                      {t('users:invite')}
                    </Button>
                  </Can>
                )
              }
            />
          ) : (
            <>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('users:columns.user')}</Th>
                      <Th>{t('users:columns.roles')}</Th>
                      <Th>{t('users:columns.scope')}</Th>
                      <Th>{t('users:columns.mfa')}</Th>
                      <Th>{t('users:columns.lastLogin')}</Th>
                      <Th>{t('users:columns.status')}</Th>
                      <Th align="right">
                        <span className="sr-only">{t('users:columns.actions')}</span>
                      </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <Tr key={user.id}>
                        <Td>
                          <UserCell
                            avatar={
                              <Avatar
                                name={user.fullName}
                                size="sm"
                                tone={user.id === session.user.id ? 'grad' : undefined}
                              />
                            }
                            name={
                              <Link to="/admin/users/$userId" params={{ userId: user.id }}>
                                {user.fullName}
                              </Link>
                            }
                            subtitle={user.email}
                          />
                        </Td>
                        <Td>
                          <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                            {user.roles.map((role) => (
                              <Badge key={role.roleId} tone="violet">
                                {role.roleName}
                              </Badge>
                            ))}
                          </span>
                        </Td>
                        <Td className="t-13">
                          {user.roles.every((role) => role.propertyId === null)
                            ? t('users:allProperties')
                            : session.user.properties[0]?.name}
                        </Td>
                        <Td>
                          {user.mfaEnabled ? (
                            <Badge tone="green">
                              <Check className="ico ico-14" aria-hidden="true" />
                              {t('users:mfaEnabled')}
                            </Badge>
                          ) : (
                            <Badge tone="amber">
                              <AlertTriangle className="ico ico-14" aria-hidden="true" />
                              {t('users:mfaDisabled')}
                            </Badge>
                          )}
                        </Td>
                        <Td numeric className="t-13">
                          {user.lastLoginAt
                            ? formatRelative(user.lastLoginAt, i18n.language)
                            : t('users:never')}
                        </Td>
                        <Td>
                          <Badge
                            tone={
                              user.status === 'ACTIVE'
                                ? 'green'
                                : user.status === 'INVITED'
                                  ? 'blue'
                                  : 'neutral'
                            }
                            dot
                          >
                            {t(`users:status.${user.status}`)}
                          </Badge>
                        </Td>
                        <Td align="right">
                          <RowActions>
                            <Can permission="user.update">
                              <IconButton
                                label={`${t('users:actions.edit')} — ${user.fullName}`}
                                onClick={() => {
                                  setDrawerUser(user);
                                  setDrawerOpen(true);
                                }}
                              >
                                <Pencil className="ico ico-16" aria-hidden="true" />
                              </IconButton>
                            </Can>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <IconButton
                                  label={`${t('common:actions.openMenu')} — ${user.fullName}`}
                                >
                                  <MoreHorizontal className="ico ico-16" aria-hidden="true" />
                                </IconButton>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link to="/admin/users/$userId" params={{ userId: user.id }}>
                                    <ShieldCheck className="ico" aria-hidden="true" />
                                    {t('users:detail.title')}
                                  </Link>
                                </DropdownMenuItem>
                                {user.status === 'ACTIVE' ? (
                                  <DropdownMenuItem disabled>
                                    <ShieldOff className="ico" aria-hidden="true" />
                                    {t('users:actions.deactivate')}
                                  </DropdownMenuItem>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </RowActions>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>

              {query.hasNextPage ? (
                <div className="load-more">
                  <Button
                    variant="secondary"
                    loading={query.isFetchingNextPage}
                    onClick={() => void query.fetchNextPage()}
                  >
                    {t('common:actions.loadMore')}
                  </Button>
                  <span className="t-13 muted">
                    {t('common:pagination.showing', { count: users.length })}
                  </span>
                </div>
              ) : null}
            </>
          )}
        </Card>
      </div>

      <UserFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        roles={roles}
        onInvited={setInvited}
        {...(drawerUser ? { user: drawerUser } : {})}
      />

      <DeliveryLinkDialog
        link={invited?.activation ?? null}
        recipient={invited ? { fullName: invited.fullName, email: invited.email } : null}
        onClose={() => setInvited(null)}
      />
    </Container>
  );
}
